import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Bar, CartesianGrid, ComposedChart, Tooltip, XAxis, YAxis } from 'recharts'
import { RechartsChartTooltipProvider, StableRechartsChartTooltip } from './ChartTooltip'
import { defaultFormatter } from './formatters'
import './chart.css'

const DEFAULT_BAR_SLOT_WIDTH = 36
const DEFAULT_Y_AXIS_WIDTH = 48
const Y_AXIS_PANEL_WIDTH = 56
const X_AXIS_HEIGHT = 28
const X_TICK_MIN_WIDTH = 56
const SCROLL_TOOLTIP_WIDTH = 240
const SCROLL_TOOLTIP_HEIGHT = 180
const SCROLL_TOOLTIP_OFFSET = 12

function getStackedMax(data, series) {
  if (!data?.length || !series?.length) return 0
  const stackKeys = series.filter((item) => item.stackId).map((item) => item.key)
  if (stackKeys.length > 0) {
    return Math.max(
      0,
      ...data.map((row) => stackKeys.reduce((sum, key) => sum + (Number(row[key]) || 0), 0))
    )
  }
  return Math.max(
    0,
    ...data.flatMap((row) => series.map((item) => Number(row[item.key]) || 0))
  )
}

function getNiceMax(value) {
  if (!Number.isFinite(value) || value <= 0) return 10
  const magnitude = 10 ** Math.floor(Math.log10(value))
  return Math.ceil(value / magnitude) * magnitude
}

function getChartAnchorElement(scrollContainerRef) {
  return scrollContainerRef.current?.querySelector('.recharts-wrapper') ?? scrollContainerRef.current
}

function computeTooltipPosition(anchorX, anchorY, width, height, bounds) {
  const margin = 8
  const maxX = bounds?.width != null ? bounds.width - margin : window.innerWidth - margin
  const maxY = bounds?.height != null ? bounds.height - margin : window.innerHeight - margin

  let x = anchorX + SCROLL_TOOLTIP_OFFSET
  let y = anchorY - height - SCROLL_TOOLTIP_OFFSET

  if (y < margin) {
    y = anchorY + SCROLL_TOOLTIP_OFFSET
  }
  if (y + height > maxY) {
    y = Math.max(margin, maxY - height)
  }
  if (x + width > maxX) {
    x = anchorX - width - SCROLL_TOOLTIP_OFFSET
  }
  if (x < margin) {
    x = margin
  }

  return { x, y }
}

function TooltipPortalBridge({ tooltipProps, onTooltipChange }) {
  const latestPropsRef = useRef(tooltipProps)
  latestPropsRef.current = tooltipProps

  useLayoutEffect(() => {
    if (tooltipProps.active && tooltipProps.payload?.length) {
      onTooltipChange(latestPropsRef.current)
      return
    }
    onTooltipChange(null)
  }, [
    tooltipProps.active,
    tooltipProps.label,
    tooltipProps.coordinate?.x,
    tooltipProps.coordinate?.y,
    onTooltipChange,
  ])

  useEffect(() => () => onTooltipChange(null), [onTooltipChange])

  return null
}

function PortaledScrollTooltip({ tooltipProps, scrollContainerRef, tooltipLayerRef, renderContent }) {
  const tooltipRef = useRef(null)
  const [position, setPosition] = useState({ x: 0, y: 0 })

  const updatePosition = useCallback(() => {
    if (!tooltipProps?.coordinate || !tooltipLayerRef.current) return

    const anchorEl = getChartAnchorElement(scrollContainerRef)
    if (!anchorEl) return

    const anchorRect = anchorEl.getBoundingClientRect()
    const layerRect = tooltipLayerRef.current.getBoundingClientRect()
    const tooltipWidth = tooltipRef.current?.offsetWidth || SCROLL_TOOLTIP_WIDTH
    const tooltipHeight = tooltipRef.current?.offsetHeight || SCROLL_TOOLTIP_HEIGHT
    const anchorX = anchorRect.left - layerRect.left + tooltipProps.coordinate.x
    const anchorY = anchorRect.top - layerRect.top + tooltipProps.coordinate.y
    const nextPosition = computeTooltipPosition(
      anchorX,
      anchorY,
      tooltipWidth,
      tooltipHeight,
      { width: layerRect.width, height: layerRect.height }
    )
    setPosition((prev) => (
      prev.x === nextPosition.x && prev.y === nextPosition.y ? prev : nextPosition
    ))
  }, [tooltipProps, scrollContainerRef, tooltipLayerRef])

  useLayoutEffect(() => {
    updatePosition()
  }, [updatePosition])

  useEffect(() => {
    const scrollEl = scrollContainerRef.current
    const handleReposition = () => requestAnimationFrame(updatePosition)
    scrollEl?.addEventListener('scroll', handleReposition, { passive: true })
    window.addEventListener('resize', handleReposition)
    return () => {
      scrollEl?.removeEventListener('scroll', handleReposition)
      window.removeEventListener('resize', handleReposition)
    }
  }, [scrollContainerRef, updatePosition])

  useLayoutEffect(() => {
    if (!tooltipRef.current) return undefined
    const observer = new ResizeObserver(() => requestAnimationFrame(updatePosition))
    observer.observe(tooltipRef.current)
    return () => observer.disconnect()
  }, [updatePosition, tooltipProps.label])

  if (!tooltipProps?.coordinate) return null

  return (
    <div
      ref={tooltipRef}
      className="scrollable-bar-chart__tooltip-portal"
      style={{ left: position.x, top: position.y }}
    >
      {renderContent(tooltipProps)}
    </div>
  )
}

function ScrollHint({ direction, onClick, label }) {
  return (
    <button
      type="button"
      className={`scrollable-bar-chart__hint scrollable-bar-chart__hint--${direction}`}
      onClick={onClick}
      aria-label={label}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        {direction === 'right' ? (
          <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        ) : (
          <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        )}
      </svg>
    </button>
  )
}

const ScrollableStackedBarChart = ({
  data,
  xKey,
  series,
  height = 260,
  emptyMessage = 'No data available',
  valueFormatter,
  labelFormatter,
  xTickFormatter,
  barSize = 10,
  barCategoryGap = '25%',
  barGap = 2,
  barSlotWidth = DEFAULT_BAR_SLOT_WIDTH,
  yAxisWidth = DEFAULT_Y_AXIS_WIDTH,
}) => {
  const chartRef = useRef(null)
  const scrollRef = useRef(null)
  const tooltipLayerRef = useRef(null)
  const [viewportWidth, setViewportWidth] = useState(0)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [portalTooltipProps, setPortalTooltipProps] = useState(null)
  const handlePortalTooltipChange = useCallback((next) => setPortalTooltipProps(next), [])

  const portalTooltipContent = useMemo(
    () => function PortalBridge(props) {
      return <TooltipPortalBridge tooltipProps={props} onTooltipChange={handlePortalTooltipChange} />
    },
    [handlePortalTooltipChange]
  )

  const updateScrollMetrics = useCallback(() => {
    const chart = chartRef.current
    const scroll = scrollRef.current
    if (chart) {
      setViewportWidth(Math.max(0, chart.clientWidth - Y_AXIS_PANEL_WIDTH))
    }
    if (!scroll) return
    const { scrollLeft, scrollWidth, clientWidth } = scroll
    setCanScrollLeft(scrollLeft > 4)
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 4)
  }, [])

  useEffect(() => {
    const chart = chartRef.current
    const scroll = scrollRef.current
    if (!chart) return undefined

    const frame = requestAnimationFrame(updateScrollMetrics)
    const observer = new ResizeObserver(() => {
      updateScrollMetrics()
    })
    observer.observe(chart)
    scroll?.addEventListener('scroll', updateScrollMetrics, { passive: true })
    window.addEventListener('resize', updateScrollMetrics)

    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
      scroll?.removeEventListener('scroll', updateScrollMetrics)
      window.removeEventListener('resize', updateScrollMetrics)
    }
  }, [updateScrollMetrics, data, barSlotWidth])

  const empty = !data || data.length === 0
  const normalizedSeries = useMemo(
    () =>
      (series || []).map((item) => ({
        ...item,
        formatter: item.formatter || valueFormatter || defaultFormatter,
      })),
    [series, valueFormatter]
  )

  const yDomain = useMemo(() => {
    const max = getNiceMax(getStackedMax(data, normalizedSeries))
    return [0, max]
  }, [data, normalizedSeries])

  const categoryCount = data?.length || 0
  const minPlotWidth = Math.max(1, categoryCount) * barSlotWidth
  const plotWidth = Math.max(viewportWidth > 0 ? viewportWidth : minPlotWidth, minPlotWidth)
  const isScrollable = plotWidth > viewportWidth + 1
  const xTickInterval = useMemo(() => {
    if (categoryCount <= 1) return 0
    const available = Math.max(plotWidth - 16, X_TICK_MIN_WIDTH)
    const maxTicks = Math.max(2, Math.floor(available / X_TICK_MIN_WIDTH))
    if (categoryCount <= maxTicks) return 0
    return Math.ceil(categoryCount / maxTicks) - 1
  }, [categoryCount, plotWidth])
  const chartMargins = { top: 10, bottom: X_AXIS_HEIGHT, left: 0, right: 16 }
  const yAxisMargins = { top: 10, bottom: X_AXIS_HEIGHT, left: 0, right: 8 }

  const scrollByDirection = (direction) => {
    const node = scrollRef.current
    if (!node) return
    const delta = direction === 'right' ? node.clientWidth * 0.75 : -node.clientWidth * 0.75
    node.scrollBy({ left: delta, behavior: 'smooth' })
  }

  if (empty) {
    return (
      <div className="rg-chart__empty scrollable-bar-chart__empty" style={{ height }}>
        {emptyMessage}
      </div>
    )
  }

  return (
    <div className="scrollable-bar-chart" ref={chartRef} style={{ height }}>
      <div
        className="scrollable-bar-chart__y-axis"
        style={{ width: Y_AXIS_PANEL_WIDTH, minWidth: Y_AXIS_PANEL_WIDTH, height }}
      >
        <ComposedChart
          width={Y_AXIS_PANEL_WIDTH}
          height={height}
          data={data}
          margin={yAxisMargins}
        >
          <YAxis
            type="number"
            domain={yDomain}
            width={yAxisWidth}
            tickCount={5}
            allowDecimals={false}
            stroke="var(--chart-muted)"
            fontSize={11}
            tickLine={false}
            axisLine={{ stroke: 'var(--chart-border)' }}
            tick={{ fill: 'var(--chart-muted)' }}
          />
          {normalizedSeries[0] && (
            <Bar
              dataKey={normalizedSeries[0].key}
              fill="transparent"
              fillOpacity={0}
              stroke="none"
              stackId={normalizedSeries[0].stackId}
              isAnimationActive={false}
            />
          )}
        </ComposedChart>
      </div>

      <div
        className={`scrollable-bar-chart__scroll-wrap${isScrollable ? ' scrollable-bar-chart__scroll-wrap--scrollable' : ''}`}
      >
        {isScrollable && canScrollLeft && (
          <ScrollHint
            direction="left"
            label="Scroll chart left"
            onClick={() => scrollByDirection('left')}
          />
        )}
        {isScrollable && canScrollRight && (
          <ScrollHint
            direction="right"
            label="Scroll chart right"
            onClick={() => scrollByDirection('right')}
          />
        )}

        <RechartsChartTooltipProvider
          series={normalizedSeries}
          options={{
            valueFormatter,
            labelFormatter,
            className: 'rg-chart__tooltip--scrollable',
          }}
        >
          <div
            className={`scrollable-bar-chart__scroll${isScrollable ? ' scrollable-bar-chart__scroll--scrollable' : ''}`}
            ref={scrollRef}
            aria-label="Chart (scroll horizontally)"
          >
            <div
              className="scrollable-bar-chart__canvas"
              style={{ width: plotWidth, minWidth: plotWidth }}
            >
              <ComposedChart
                width={plotWidth}
                height={height}
                data={data}
                margin={chartMargins}
                barSize={barSize}
                barCategoryGap={barCategoryGap}
                barGap={barGap}
              >
                <CartesianGrid stroke="var(--chart-grid)" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  type="category"
                  dataKey={xKey}
                  stroke="var(--chart-muted)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={{ stroke: 'var(--chart-border)' }}
                  interval={xTickInterval}
                  minTickGap={24}
                  tickFormatter={xTickFormatter}
                  height={X_AXIS_HEIGHT}
                />
                <YAxis type="number" domain={yDomain} hide width={0} />
                <Tooltip
                  content={portalTooltipContent}
                  wrapperStyle={{ display: 'none' }}
                  cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                />
                {normalizedSeries.map((item) => (
                  <Bar
                    key={item.key}
                    dataKey={item.key}
                    fill={`var(${item.colorVar})`}
                    radius={[4, 4, 0, 0]}
                    name={item.label}
                    stackId={item.stackId}
                    maxBarSize={barSize}
                    isAnimationActive={false}
                  />
                ))}
              </ComposedChart>
            </div>
          </div>

          <div
            ref={tooltipLayerRef}
            className="scrollable-bar-chart__tooltip-layer"
            aria-hidden={!portalTooltipProps}
          >
            {portalTooltipProps?.active && portalTooltipProps?.payload?.length > 0 && (
              <PortaledScrollTooltip
                tooltipProps={portalTooltipProps}
                scrollContainerRef={scrollRef}
                tooltipLayerRef={tooltipLayerRef}
                renderContent={(props) => <StableRechartsChartTooltip {...props} />}
              />
            )}
          </div>
        </RechartsChartTooltipProvider>
      </div>
    </div>
  )
}

export default ScrollableStackedBarChart
